import React, { useContext, useMemo } from "react";

import { useLocalization } from "@fluent/react";
import { Button, IconButton, Snackbar } from "@narmi/design_system";

import { SelectionContext } from "./SelectionContext";
import styles from "./BulkActionSnackbar.module.scss";

interface BulkActionSnackbarProps {
  /** Omit to render the Accept button as disabled. */
  onAccept?: () => void;
  /** Omit to render the Reject button as disabled. */
  onReject?: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}

/**
 * Page-level snackbar for bulk Accept/Reject. Lives at the page level so it
 * survives table re-renders driven by refetch. The page owns the mutations
 * and passes ``on*``/``is*`` props; the snackbar itself is presentational.
 */
const BulkActionSnackbar = ({
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
}: BulkActionSnackbarProps) => {
  const { l10n } = useLocalization();
  const { selectionState, setSelectionState } = useContext(SelectionContext);

  const selectedCount = useMemo(
    () => Object.keys(selectionState).length,
    [selectionState]
  );
  // Bulk Accept/Reject only operate on rows that carry a suggested_match —
  // anything else would silently drop out of the request payload. Keep the
  // button labels in lockstep with what we'd actually send so "Accept N"
  // never overstates the work.
  const actionableCount = useMemo(
    () =>
      Object.values(selectionState).filter(
        (row) => row.suggested_match?.id != null
      ).length,
    [selectionState]
  );

  if (selectedCount === 0) return null;

  const clearSelection = () => setSelectionState({});
  const anyActionPending = isAccepting || isRejecting;
  const hasActionable = actionableCount > 0;

  return (
    <div
      className={styles["bulk-snackbar-wrapper"]}
      data-testid="receipts-bulk-snackbar"
    >
      <Snackbar isActive>
        <Snackbar.Text>
          <div className={styles["bulk-snackbar-text"]}>
            <span>
              <b>
                {l10n.getString("receipts-bulk-selected", {
                  count: selectedCount,
                })}
              </b>
            </span>
            <IconButton
              name="x"
              kind="action"
              textSize="l"
              label={l10n.getString("receipts-bulk-clear")}
              onClick={clearSelection}
            />
          </div>
        </Snackbar.Text>
        <Snackbar.Divider />
        <Snackbar.ButtonGroup>
          <Button
            kind="secondary"
            label={l10n.getString("receipts-bulk-reject", {
              count: actionableCount,
            })}
            testId="receipts-bulk-snackbar-reject"
            disabled={!onReject || !hasActionable || anyActionPending}
            isLoading={isRejecting}
            onClick={onReject ?? (() => {})}
          />
          <Button
            kind="primary"
            label={l10n.getString("receipts-bulk-accept", {
              count: actionableCount,
            })}
            testId="receipts-bulk-snackbar-accept"
            disabled={!onAccept || !hasActionable || anyActionPending}
            isLoading={isAccepting}
            onClick={onAccept ?? (() => {})}
          />
        </Snackbar.ButtonGroup>
      </Snackbar>
    </div>
  );
};

export default BulkActionSnackbar;
